import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { createRoot, type Root } from "react-dom/client";
import { createElement } from "react";
import {
  SignedAgreementDocument,
  type SignedAgreementPayload,
} from "./SignedAgreementDocument";

/**
 * Render the signed agreement payload into an off-screen iframe sandbox and
 * rasterize it into a PDF blob.
 *
 * Why an iframe rather than a node clone:
 * - We render a fresh React tree from the payload, so the PDF cannot drift
 *   from the contract the user signed (no `cloneNode(true)` of the live DOM).
 * - The iframe is positioned just outside the viewport (not `display:none`,
 *   not `opacity:0`, not `z-index:-1`) so the browser fully lays it out and
 *   `html2canvas` can rasterize real pixels — the previous hidden-iframe
 *   approach produced a blank white canvas.
 */
export async function captureAgreementToPdf(
  payload: SignedAgreementPayload,
): Promise<Blob> {
  if (!payload) throw new Error("Agreement payload is required");

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.title = "agreement-capture-sandbox";
  iframe.style.position = "fixed";
  iframe.style.top = "0";
  // Park the iframe just outside the visible viewport so it is fully laid out
  // and rasterizable, but invisible to the user.
  iframe.style.left = "calc(100vw + 24px)";
  iframe.style.width = "820px";
  iframe.style.height = "1200px";
  iframe.style.border = "0";
  iframe.style.background = "#ffffff";
  iframe.style.pointerEvents = "none";

  document.body.appendChild(iframe);

  let root: Root | null = null;

  try {
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) throw new Error("Could not access iframe document");

    doc.open();
    doc.write(
      "<!doctype html><html><head><meta charset='utf-8'></head><body></body></html>",
    );
    doc.close();

    // Mirror parent stylesheets so Tailwind classes / design tokens render
    // identically inside the sandbox.
    document
      .querySelectorAll('link[rel="stylesheet"], style')
      .forEach((el) => {
        doc.head.insertAdjacentHTML("beforeend", el.outerHTML);
      });

    // Force a clean light-theme paper look regardless of the active theme.
    const themeStyle = doc.createElement("style");
    themeStyle.textContent = `
      :root, html, body {
        --background: 0 0% 100%;
        --foreground: 222.2 47.4% 11.2%;
        --muted: 210 40% 96.1%;
        --muted-foreground: 215.4 16.3% 46.9%;
        --border: 214.3 31.8% 91.4%;
        --card: 0 0% 100%;
        --card-foreground: 222.2 47.4% 11.2%;
        --popover: 0 0% 100%;
        --popover-foreground: 222.2 47.4% 11.2%;
        background: #ffffff !important;
        color: #1a1a1a;
      }
      html, body { margin: 0; padding: 0; background: #ffffff; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
          "Helvetica Neue", Arial, "Noto Sans Arabic", sans-serif;
      }
      #agreement-capture-root {
        width: 794px;
        background: #ffffff;
      }
      #agreement-capture-root * {
        max-height: none !important;
        overflow: visible !important;
      }
    `;
    doc.head.appendChild(themeStyle);

    const mount = doc.createElement("div");
    mount.id = "agreement-capture-root";
    mount.dir = payload.lang === "ar" ? "rtl" : "ltr";
    doc.body.appendChild(mount);

    // Render a fresh React tree from the payload — never a clone of the live DOM.
    root = createRoot(mount);
    root.render(
      createElement(SignedAgreementDocument, { ...payload, forPrint: true }),
    );

    // Wait for fonts, images, and layout to settle inside the iframe.
    if ((doc as Document & { fonts?: FontFaceSet }).fonts?.ready) {
      try {
        await (doc as Document & { fonts: FontFaceSet }).fonts.ready;
      } catch {
        /* ignore */
      }
    }

    // Allow React to commit then images to load.
    await new Promise((r) => win.requestAnimationFrame(() => r(null)));
    await new Promise((r) => win.requestAnimationFrame(() => r(null)));

    await Promise.all(
      Array.from(mount.querySelectorAll("img")).map((img) => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return img.decode().catch(
          () =>
            new Promise<void>((resolve) => {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }),
        );
      }),
    );

    await new Promise((r) => win.requestAnimationFrame(() => r(null)));

    // Resize iframe to fit the rendered content so html2canvas sees full height.
    const fullHeight = Math.max(mount.scrollHeight, mount.offsetHeight, 1200);
    iframe.style.height = `${fullHeight + 40}px`;

    await new Promise((r) => win.requestAnimationFrame(() => r(null)));

    const canvas = await html2canvas(mount, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      windowWidth: mount.scrollWidth,
      windowHeight: mount.scrollHeight,
    });

    if (!canvas.width || !canvas.height) {
      throw new Error("Agreement capture produced an empty canvas");
    }

    // Sanity check: confirm the canvas is not entirely white/transparent.
    const ctx = canvas.getContext("2d");
    if (ctx) {
      const sampleW = Math.min(canvas.width, 64);
      const sampleH = Math.min(canvas.height, 64);
      const samples = [
        ctx.getImageData(0, 0, sampleW, sampleH).data,
        ctx.getImageData(
          Math.max(0, Math.floor(canvas.width / 2) - sampleW / 2),
          Math.max(0, Math.floor(canvas.height / 2) - sampleH / 2),
          sampleW,
          sampleH,
        ).data,
        ctx.getImageData(
          Math.max(0, canvas.width - sampleW),
          Math.max(0, canvas.height - sampleH),
          sampleW,
          sampleH,
        ).data,
      ];
      const hasContent = samples.some((data) => {
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          // any pixel that is not white-ish and not fully transparent counts.
          if (a > 10 && (r < 245 || g < 245 || b < 245)) return true;
        }
        return false;
      });
      if (!hasContent) {
        throw new Error("Agreement capture is blank (all-white render)");
      }
    }

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidthMm = 210;
    const pageHeightMm = 297;
    const imgWidthMm = pageWidthMm;
    const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;
    const imgData = canvas.toDataURL("image/jpeg", 0.95);

    let heightLeft = imgHeightMm;
    let position = 0;

    pdf.addImage(imgData, "JPEG", 0, position, imgWidthMm, imgHeightMm);
    heightLeft -= pageHeightMm;

    while (heightLeft > 0) {
      position -= pageHeightMm;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, imgWidthMm, imgHeightMm);
      heightLeft -= pageHeightMm;
    }

    return pdf.output("blob");
  } finally {
    try {
      root?.unmount();
    } catch {
      /* ignore */
    }
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }
}
